import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import cx from 'classnames';
import { Text, Button, SecurityHelper } from 'sarsaparilla';
import { isDayManagedByLottery } from 'ui-permit/src/utils/availability-utils';
import StickyTableHeaderHelper from 'ui-permit/shared-components/detailed-availability/StickyTableHeaderHelper';

import { Grid, Row, GridCell } from './detailed-availability-page/Grid';
import AvailabilityTableHeader from './detailed-availability-page/AvailabilityTableHeader';

import AvailabilityCell from './AvailabilityCell';
import { MT_WHITNEY_PERMIT_ID } from '../utils/inyoUtils';
import { AVAILABILITY_KEY_MAP_FORMAT } from '../constants';

const propTypes = {
  divisions: PropTypes.array,
  availabilities: PropTypes.object,
  permitDate: PropTypes.string,
  permit: PropTypes.object,
  selectedStartDate: PropTypes.string,
  selectedDivision: PropTypes.object,
  onSelectToggle: PropTypes.func,
  selectedGroupSize: PropTypes.number.isRequired,
  aggSeasonRules: PropTypes.array.isRequired,
  onDivisionDetailsClick: PropTypes.func,
  numOfCols: PropTypes.number,
  isSmallerGrid: PropTypes.bool,
  isFullView: PropTypes.bool,
};

const defaultProps = {};

export default class OvernightAvailabilityGrid extends React.Component {

  constructor(props) {
    super(props);
    this.state = {
      sort: {
        column: 'name',
        isSorted: true,
        isAsc: true
      }
    };
    this.state.divisions = this.getSortedDivisions(props.divisions);
    this.onColumnSortToggle = this.onColumnSortToggle.bind(this);
    this.onDistrictSortToggle = this.onDistrictSortToggle.bind(this);
    this.onNameSortToggle = this.onNameSortToggle.bind(this);
    this.onCodeSortToggle = this.onCodeSortToggle.bind(this);
  }
  componentDidUpdate(prevProps) {
    if (prevProps.divisions !== this.props.divisions) {
      this.setState({
        divisions: this.getSortedDivisions(this.props.divisions)
      });
    }
  }

  onColumnSortToggle(columnName, isSorted, isAsc) {
    this.setState({
      sort: {
        column: columnName,
        isSorted,
        isAsc
      }
    }, () => {
      this.setState({
        divisions: this.getSortedDivisions(this.state.divisions)
      });
    });
  }

  onCodeSortToggle(isSorted, isAsc) {
    this.onColumnSortToggle('code', isSorted, isAsc);
  }

  onNameSortToggle(isSorted, isAsc) {
    this.onColumnSortToggle('name', isSorted, isAsc);
  }

  onDistrictSortToggle(isSorted, isAsc) {
    this.onColumnSortToggle('district', isSorted, isAsc);
  }

  onSelectToggle(division, index) {
    if (this.props.onSelectToggle) {
      if (index >= 0) {
        let permitDate = this.props.permitDate;
        if (permitDate) {
          permitDate = moment.utc(permitDate).clone().add(index, 'day').toISOString();
        }
        this.props.onSelectToggle(division, permitDate);
      }
      else {
        this.props.onSelectToggle(division);
      }
    }
  }

  getAvailabilityCell(division, index) {
    let isSelectedDivision = false;
    if (this.props.selectedDivision && this.props.selectedDivision.id === division.id) {
      isSelectedDivision = true;
    }
    const permitDate = moment.utc(this.props.permitDate);
    const date = permitDate.clone().add(index, 'day');
    const isSelectedCell = isSelectedDivision && date.isSame(moment.utc(this.props.selectedStartDate));
    const dateKey = date.clone().utc().startOf('month').toISOString();
    const avail = this.props.availabilities[dateKey];
    let count = null;
    let isWalkup = false;
    const isLottery = isDayManagedByLottery(date, this.props);
    if (avail && avail.result) {
      const divAvail = avail.result[division.id];
      if (divAvail) {
        const nums = divAvail.result.nums;
        const walks = divAvail.result.walkUps;
        if (nums) {
          count = nums[moment.utc(date).format(AVAILABILITY_KEY_MAP_FORMAT)];
        }
        // walkup cells for Mt. Whitney should show as unavailable
        if (walks && this.props.permit.id !== MT_WHITNEY_PERMIT_ID) {
          isWalkup = walks[moment.utc(date).format(AVAILABILITY_KEY_MAP_FORMAT)];
        }
      }
    }
    return (
      <AvailabilityCell
        key={index}
        count={count}
        selectedGroupSize={this.props.selectedGroupSize}
        isWalkup={isWalkup}
        isLottery={isLottery}
        isSelected={isSelectedCell}
        onSelectToggle={this.onSelectToggle.bind(this, division, index)} />
    );
  }

  getSortedDivisions(divisions) {
    const ret = [];
    divisions.forEach((division) => {
      ret[ret.length] = division;
    });
    if (this.state && this.state.sort.isSorted) {
      ret.sort((a, b) => {
        let o1 = a[this.state.sort.column];
        let o2 = b[this.state.sort.column];
        if (!this.state.sort.isAsc) {
          o1 = b[this.state.sort.column];
          o2 = a[this.state.sort.column];
        }
        // commented as per DEFECT-2060
        // if (this.state.sort.column === 'code') {
        //   o1 = o1.replace(/([a-zA-Z ])/g, '');
        //   o2 = o2.replace(/([a-zA-Z ])/g, '');
        //   o1 = toNumber(o1);
        //   o2 = toNumber(o2);
        // }
        if (o1 > o2) {
          return 1;
        }
        if (o1 < o2) {
          return -1;
        }
        return 0;
      });
    }
    return ret;
  }

  renderRow(division) {
    const isMobileApp = SecurityHelper.isMobileApp();
    const { isSmallerGrid, numOfCols } = this.props;
    const { name, code, district } = division;
    const hasDivisionDetailsLink = !!this.props.onDivisionDetailsClick;

    let divisionName = <Text>{name}</Text>;
    if (!isMobileApp && hasDivisionDetailsLink) {
      divisionName = (
          <Text>
              <Button
                  appearance="link"
                  onClick={() => this.props.onDivisionDetailsClick(division.id)} >
                  {name}
              </Button>
          </Text>
      );
    }

    const divisionComponent = !isSmallerGrid ? divisionName : (
      <>
        {divisionName}
        <Text>{district}</Text>
      </>
    );

    return (
      <Row key={division.id}>
        {!isSmallerGrid && (
          <GridCell>
            <div>{code}</div>
          </GridCell>
        )}
        <GridCell>
          <div>{divisionComponent}</div>
        </GridCell>
        {!isSmallerGrid && (
          <GridCell>
            <div>
                {district}
            </div>
          </GridCell>
        )}
        {Array(numOfCols).fill().map((_, i) => (
          this.getAvailabilityCell(division, i)
        ))}
      </Row>
    );
  }

  render() {
    const { numOfCols, isSmallerGrid, isFullView } = this.props;

    return (
      <div className="per-availability-table-container">
          <StickyTableHeaderHelper scrollingContainerId="per-availability-main" isFullView={isFullView}>
              <div aria-hidden="true" className="sticky-availability-table-header">
                
                <Grid className={cx("detailed-availability-grid-new", {
                  "has-area-data": !isSmallerGrid,
                  "mobile-app": isSmallerGrid,
                })}>
                  <AvailabilityTableHeader
                        divisionType="Sites"
                        hasAreaData={!isSmallerGrid}
                        sort={this.state.sort}
                        startDate={this.props.permitDate}
                        onNameSortToggle={this.onNameSortToggle}
                        onAreaSortToggle={this.onDistrictSortToggle}
                        onCodeSortToggle={this.onCodeSortToggle}
                        numOfCols={numOfCols}
                  />
                </Grid>
              </div>
          </StickyTableHeaderHelper>
          <Grid
            className={cx("detailed-availability-grid-new", {
                "has-area-data": !isSmallerGrid,
                "mobile-app": isSmallerGrid,
            })}
            caption="Availability by Sites and Dates">
            <AvailabilityTableHeader
              divisionType="Sites"
              hasAreaData={!isSmallerGrid}
              sort={this.state.sort}
              startDate={this.props.permitDate}
              onNameSortToggle={this.onNameSortToggle}
              onAreaSortToggle={this.onDistrictSortToggle}
              onCodeSortToggle={this.onCodeSortToggle}
              numOfCols={numOfCols}
            />
            {this.state.divisions.map((division, i) => {
              return this.renderRow(division, i);
            })}
          </Grid>
      </div>
    );
  }
}

OvernightAvailabilityGrid.propTypes = propTypes;
OvernightAvailabilityGrid.defaultProps = defaultProps;
